import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Target } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { CtaBand } from "@/components/CtaBand";
import { PillButton } from "@/components/PillButton";
import { Icon } from "@/lib/icons";
import { IMG } from "@/lib/images";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { ErrorState } from "@/components/States";
import { Skeleton } from "@/components/ui/skeleton";
import { useService, useServices, useProducts, useFaqs } from "@/hooks/useSiteData";
import NotFoundPage from "@/pages/NotFoundPage";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const { data: s, isLoading, isError, error, refetch } = useService(slug);
  const { data: all = [] } = useServices();
  const { data: products = [] } = useProducts();
  const { data: faqs = [] } = useFaqs();

  if (isLoading) return <div className="container-x py-20"><Skeleton className="h-8 w-40" /><Skeleton className="mt-6 h-14 w-2/3" /><Skeleton className="mt-4 h-5 w-1/2" /></div>;
  if (isError && error?.response?.status === 404) return <NotFoundPage />;
  if (isError) return <div className="container-x py-20"><ErrorState onRetry={refetch} /></div>;

  const related = products.filter((p) => s.related_products?.includes(p.slug));
  const relevantFaqs = faqs.filter((f) => ["Engagement", "General"].includes(f.category)).slice(0, 3);

  return (
    <>
      <Seo title={s.title} description={s.tagline || s.description?.slice(0, 155)} path={`/services/${s.slug}`} />
      <PageHero eyebrow="Service" title={s.title} lead={s.tagline} crumbs={[{ label: "Services", to: "/services" }, { label: s.title }]} image={s.image} testId="service-detail-hero">
        <div className="mt-10 flex flex-col gap-3 sm:flex-row">
          <PillButton to={`/contact?interest=${encodeURIComponent(s.title)}`} variant="primary" size="lg" testId="service-book-consultation-button">Book a consultation</PillButton>
          <PillButton to="/services" variant="outline-light" size="lg" testId="service-back-to-services-button">All services</PillButton>
        </div>
      </PageHero>

      <section className="section paper">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <Reveal className="photo aspect-[16/9] rounded-3xl"><img src={s.image} alt={s.title} /></Reveal>
            <Reveal className="mt-10"><span className="eyebrow">Service overview</span><h2 className="section-title mt-4">What we do</h2><p className="lead mt-5">{s.description}</p></Reveal>
            <Reveal className="mt-12">
              <span className="eyebrow">Capabilities</span>
              <Stagger className="mt-6 grid gap-3 sm:grid-cols-2">
                {s.capabilities.map((c) => <StaggerItem key={c}><div className="flex items-start gap-3 rounded-2xl border border-border bg-white p-4" data-testid="service-capability-item"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" /><span className="text-sm font-medium">{c}</span></div></StaggerItem>)}
              </Stagger>
            </Reveal>
            <Reveal className="mt-12 grid gap-5 sm:grid-cols-2">
              <div className="photo aspect-[4/3] rounded-3xl"><img src={IMG.discussion} alt="" loading="lazy" /></div>
              <div className="rounded-3xl bg-[#0e1013] p-7 text-white">
                <span className="eyebrow eyebrow-light">Outcomes</span>
                <ul className="mt-5 space-y-3">{s.outcomes.map((o) => <li key={o} className="flex items-start gap-3 text-sm"><Target className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{o}</li>)}</ul>
              </div>
            </Reveal>
          </div>

          <aside className="space-y-5 lg:col-span-4">
            <Reveal className="rounded-3xl border border-border bg-white p-3">
              <p className="px-4 pb-2 pt-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">All services</p>
              <ul>{all.map((x) => <li key={x.id}><Link to={`/services/${x.slug}`} className={`flex items-center justify-between rounded-2xl px-4 py-3 text-sm font-medium transition-colors ${x.slug === s.slug ? "bg-[#0e1013] text-white" : "hover:bg-[#f6f6f4]"}`} data-testid={`service-sidebar-link-${x.slug}`}>{x.title}<ArrowUpRight className={`h-4 w-4 ${x.slug === s.slug ? "text-primary" : "text-muted-foreground"}`} /></Link></li>)}</ul>
            </Reveal>
            {s.engagement_model && (
              <Reveal className="rounded-3xl bg-primary p-7 text-white">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15"><Icon name={s.icon} className="h-6 w-6" /></span>
                <h3 className="mt-5 font-heading text-xl font-medium">Engagement model</h3>
                <p className="mt-3 text-sm text-white/85">{s.engagement_model}</p>
                <div className="mt-6"><PillButton to={`/contact?interest=${encodeURIComponent(s.title)}`} variant="light" size="sm" testId="service-sidebar-contact-button">Discuss your project</PillButton></div>
              </Reveal>
            )}
            {related.length > 0 && (
              <Reveal className="rounded-3xl border border-border bg-white p-6">
                <h3 className="font-heading text-lg font-medium">Relevant products</h3>
                <ul className="mt-3 divide-y divide-border">{related.map((p) => <li key={p.id}><Link to={`/products/${p.slug}`} className="group flex items-center gap-3 py-3" data-testid={`service-related-product-${p.slug}`}><span className="h-12 w-12 shrink-0 overflow-hidden rounded-xl"><img src={p.image} alt="" className="h-full w-full object-cover" /></span><span className="flex-1"><span className="block text-sm font-semibold">{p.name}</span><span className="block text-xs text-muted-foreground">{p.tagline}</span></span><ArrowUpRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" /></Link></li>)}</ul>
              </Reveal>
            )}
          </aside>
        </div>
      </section>

      {relevantFaqs.length > 0 && (
        <section className="bg-white py-16"><div className="container-x"><span className="eyebrow">Good to know</span><div className="mt-6 grid gap-5 md:grid-cols-3">{relevantFaqs.map((f) => <div key={f.id} className="rounded-3xl border border-border bg-[#f6f6f4] p-6"><h3 className="font-heading text-lg font-medium">{f.question}</h3><p className="mt-2 text-sm text-muted-foreground">{f.answer}</p></div>)}</div></div></section>
      )}
      <div className="paper pt-16 sm:pt-20"><CtaBand title={`Start your ${s.title.toLowerCase()} engagement.`} text="Share your goals and we'll propose a scoped discovery sprint within one business day." /></div>
    </>
  );
}
